import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useViewOnly } from '@/components/templates/app-shell'
import { isConnectionOwned } from '@/components/OwnedChip'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { deleteJSON } from '@/lib/api'
import type { Enrolment } from './enrolment'

export function EnrolmentDeletionDialog({
  enrolment,
  onClose,
}: {
  enrolment: Enrolment
  onClose: () => void
}) {
  const client = useQueryClient()
  const viewOnly = useViewOnly()
  const deletion = useMutation({
    mutationFn: () => deleteJSON(`/api/v1/enrolments/${enrolment.id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['enrolments'] })
      void client.invalidateQueries({ queryKey: ['course', enrolment.course_id] })
      void client.invalidateQueries({ queryKey: ['courses'] })
      onClose()
    },
  })
  if (viewOnly || isConnectionOwned(enrolment.owner)) return null
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !deletion.isPending) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete enrolment?</DialogTitle>
          <DialogDescription>
            This permanently removes the enrolment for {enrolment.person_name} in “
            {enrolment.course_title}”. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        {deletion.error && <Alert>{deletion.error.message}</Alert>}
        <DialogFooter>
          <Button variant="outline" disabled={deletion.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={deletion.isPending}
            onClick={() => deletion.mutate()}
          >
            {deletion.isPending ? 'Deleting…' : 'Delete enrolment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
